/**
 * Phase 66 Plan 02 — Topbar user dropdown.
 *
 * Trigger: avatar + email span (id="user-display" + data-testid="user-display"
 * preserved per Phase 65 contract / 66-TESTID-CONTRACT.md row A1).
 * Items: Logout (clears auth via useAuth().logout, AppShell rerenders to
 * LoginPage when user becomes null).
 *
 * Theme toggle slot reserved for Plan 06 (D-10 dark-mode).
 */

import { useNavigate } from "react-router";
import { LogOut, User as UserIcon } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/auth/AuthProvider";
import ThemeToggle from "@/components/ThemeToggle";

export default function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (!user) return null;

  async function handleLogout() {
    await logout();
    navigate("/", { replace: true });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="user-display inline-flex items-center gap-2 px-3 py-1.5 rounded hover:bg-[color:var(--color-bg)] cursor-pointer"
          aria-label="User menu"
        >
          <UserIcon size={16} aria-hidden="true" />
          <span id="user-display" data-testid="user-display" className="text-sm">
            {user.email}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[200px]">
        <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* Plan 06 D-10 — dark mode toggle. The slot div is preserved so any
            external selectors that targeted `[data-testid="theme-toggle-slot"]`
            keep working; the actual ThemeToggle button has its own testid. */}
        <div data-testid="theme-toggle-slot" className="px-1 py-0.5">
          <ThemeToggle variant="menu-item" />
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          id="logout-btn"
          data-testid="logout-btn"
          onClick={handleLogout}
          className="cursor-pointer"
        >
          <LogOut size={16} className="mr-2" aria-hidden="true" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
